<template>
  <section class="section">
    <div>
      <div style="text-align: center; font-weight: bold; margin-bottom: 15px">
        {{ $l("components.server.updateServer", "Update server") }}
      </div>
      <div style="display: flex; justify-content: center">
        <div class="stepper-wrapper">
          <div
            class="stepper-item"
            :class="{
              active: step === 1,
              completed: step === 2 || step === 3,
            }"
          >
            <div class="step-counter"></div>
            <div class="step-name">
              {{ $l("components.server.basic", "Basic") }}
            </div>
          </div>
          <div
            class="stepper-item"
            :class="{
              active: step === 2,
              completed: step === 3,
            }"
          >
            <div class="step-counter"></div>
            <div class="step-name">
              {{ $l("components.server.quotas", "Quotas") }}
            </div>
          </div>

          <div
            class="stepper-item"
            :class="{
              active: step === 3,
            }"
          >
            <div class="step-counter"></div>
            <div class="step-name">
              {{
                $l(
                  "components.server.updateRepositories",
                  "Update repositories"
                )
              }}
            </div>
          </div>
        </div>
      </div>

      <div style="display: flex; justify-content: center">
        <box style="width: 85%">
          <div class="p-4">
            <Form
              v-if="step === 1"
              ref="form"
              :model="form"
              :rules="ruleValidate"
              label-position="top"
            >
              <FormItem ref="name" prop="name">
                <template slot="label">
                  <strong>{{
                    $l("components.server.serverName", "Server name")
                  }}</strong>
                </template>

                <Input
                  v-model="form.name"
                  :placeholder="$l('components.server.enterName', 'Enter name')"
                />
              </FormItem>

              <FormItem ref="fingerprint" prop="fingerprint">
                <template slot="label">
                  <strong>{{
                    $l("components.server.fingerprint", "Identifier")
                  }}</strong>
                </template>

                <Input
                  v-model="form.fingerprint"
                  icon="md-repeat"
                  placeholder="XXXXX-XXXXX-XXXXX-XXXXX-XXXXX"
                  @on-click="newFingerprint"
                />
              </FormItem>

              <Row :gutter="16">
                <Col span="12">
                  <FormItem ref="email" prop="email">
                    <template slot="label">
                      <strong>{{
                        $l("components.server.email", "Email")
                      }}</strong>
                    </template>

                    <Input
                      v-model="form.email"
                      placeholder="contact@cyradar.com"
                    />
                  </FormItem>
                </Col>
                <Col span="12">
                  <FormItem ref="phone_number" prop="phone_number">
                    <template slot="label">
                      <strong>{{
                        $l("components.server.phoneNumber", "Phone number")
                      }}</strong>
                    </template>

                    <Input
                      v-model="form.phone_number"
                      placeholder="(+84) 24 7300 6066"
                    />
                  </FormItem>
                </Col>
              </Row>

              <FormItem ref="address" prop="address">
                <template slot="label">
                  <strong>{{
                    $l("components.server.address", "Address")
                  }}</strong>
                </template>

                <Input
                  v-model="form.address"
                  :placeholder="
                    $l(
                      'components.server.addressPlaceholder',
                      '9th Floor, FPT Tower, 10 Pham Van Bach street, Cau Giay District, Ha Noi'
                    )
                  "
                />
              </FormItem>

              <FormItem ref="company" prop="company">
                <template slot="label">
                  <strong>{{
                    $l("components.server.company", "Company")
                  }}</strong>
                </template>

                <Input
                  v-model="form.company"
                  :placeholder="
                    $l(
                      'components.server.companyPlaceholder',
                      'CyRadar Information Security Joint Stock Company'
                    )
                  "
                />
              </FormItem>

              <FormItem ref="description" prop="description">
                <template slot="label">
                  <strong>{{
                    $l("components.server.description", "Description")
                  }}</strong>
                </template>

                <Input
                  v-model="form.description"
                  type="textarea"
                  :autosize="{ minRows: 5, maxRows: 10 }"
                  :placeholder="
                    $l('components.server.description', 'Description')
                  "
                />
              </FormItem>

              <FormItem>
                <div class="field is-grouped is-grouped-right">
                  <div class="control">
                    <Button
                      style="margin-right: 8px"
                      @click="handleClickCancel"
                      >{{ $l("global.cancel", "Cancel") }}</Button
                    >
                  </div>
                  <div class="control">
                    <Button type="primary" @click="handleClickNext('form')">
                      {{ $l("global.next", "Next") }}</Button
                    >
                  </div>
                </div>
              </FormItem>
            </Form>

            <Form
              v-if="step === 2"
              ref="form"
              :model="form"
              :rules="ruleValidate"
              label-position="top"
              style="min-height: 500px"
            >
              <Row :gutter="100">
                <Col span="12">
                  <FormItem ref="agentLimit" prop="agentLimit">
                    <template slot="label">
                      <strong>{{
                        $l(
                          "components.server.numberOfAgents",
                          "Number of agents"
                        )
                      }}</strong>
                    </template>
                    <Select
                      v-model="form.agentLimit"
                      :placeholder="
                        $l(
                          'components.server.agentLimitPlaceholder',
                          'Fill in an integer and then select the value'
                        )
                      "
                      :not-found-text="
                        $l(
                          'components.server.agentLimitNotFound',
                          'Must be an integer greater than 0'
                        )
                      "
                      filterable
                      allow-create
                      @on-query-change="handleCreateAgentLimitOption"
                    >
                      <Option
                        v-for="item in listAgentLimit"
                        :value="item.value"
                        :key="item.value"
                        >{{
                          item.label === "-1"
                            ? $l("components.server.unlimit", "Unlimit")
                            : item.label
                        }}</Option
                      >
                    </Select>
                  </FormItem>
                </Col>
                <Col span="12">
                  <FormItem ref="serverLimit" prop="serverLimit">
                    <template slot="label">
                      <strong>{{
                        $l(
                          "components.server.numberOfEDRChildServers",
                          "Number of EDR child servers"
                        )
                      }}</strong>
                    </template>

                    <Input
                      v-model="form.serverLimit"
                      type="number"
                      :placeholder="
                        $l(
                          'components.server.numberOfEDRChildServers',
                          'Number of EDR child servers'
                        )
                      "
                    />
                  </FormItem>
                </Col>
              </Row>

              <Row :gutter="100">
                <Col span="12">
                  <FormItem ref="expiredAt" prop="expiredAt">
                    <template slot="label">
                      <strong>{{
                        $l("components.server.expiredAt", "ExpiredAt")
                      }}</strong>
                    </template>

                    <div class="control has-icons-right">
                      <datepicker
                        v-model="form.expiredAt"
                        name="uniquename"
                        format="dd-MM-yyyy"
                      ></datepicker>
                      <span class="icon is-small is-right">
                        <svg-icon type="mdi" :path="path"></svg-icon>
                      </span>
                    </div>
                  </FormItem>
                </Col>
                <Col span="12">
                  <FormItem ref="treeheight" prop="treeheight">
                    <template slot="label">
                      <strong>{{
                        $l(
                          "components.configurationUpdate.numberOfEDRChildLevels",
                          "Number of EDR child levels"
                        )
                      }}</strong>
                    </template>

                    <Select v-model="form.treeheight">
                      <Option
                        v-for="item in ['0', '1', '2', '3', '4', '5']"
                        :value="item"
                        :key="item"
                        >{{ item }}</Option
                      >
                    </Select>
                  </FormItem>
                </Col>
              </Row>

              <FormItem style="position: relative; top: 300px">
                <div class="field is-grouped is-grouped-right">
                  <div class="control">
                    <Button
                      style="margin-right: 8px"
                      @click="handleClickBack()"
                      >{{ $l("global.back", "Back") }}</Button
                    >
                  </div>
                  <div class="control">
                    <Button type="primary" @click="handleClickNext('form')">
                      {{ $l("global.next", "Next") }}</Button
                    >
                  </div>
                </div>
              </FormItem>
            </Form>

            <Form
              v-if="step === 3"
              ref="form"
              :model="form"
              :rules="ruleValidate"
              label-position="top"
            >
              <FormItem>
                <div style="display: flex; justify-content: space-between">
                  <div>
                    <p style="font-weight: bold">
                      {{
                        $l("components.server.total", "Total") +
                        " " +
                        listSelectedTags.length +
                        " " +
                        $l(
                          "components.server.tagsToBeUpdated",
                          "tags to be updated"
                        )
                      }}
                    </p>
                  </div>

                  <Button
                    color="#667A96"
                    style="
                      background-color: #667a96;
                      color: #fff;
                      font-weight: 500;
                    "
                    :loading="loading"
                    icon="md-add"
                    @click="showFormAddTag"
                    >{{ $l("global.add", "Add") }}</Button
                  >
                </div>
              </FormItem>

              <FormItem>
                <data-table
                  ref="table"
                  :key="currentLocale"
                  :items="listSelectedTags"
                  :per-page="10"
                  :sort-desc="true"
                  :theme="$_ui_theme_tables"
                >
                  <data-column
                    :sortable="false"
                    field="serial"
                    :label="$l('components.server.serial', 'No')"
                    width="7%"
                  />

                  <data-column
                    field="name"
                    :label="$l('components.server.name', 'Name')"
                    width="15%"
                  >
                    <template #default="{ item }">
                      <div class="truncate">
                        {{ renderTag(item.id).name }}
                      </div>
                    </template>
                  </data-column>

                  <data-column
                    field="description"
                    :label="$l('components.server.description', 'Description')"
                    width="35%"
                  >
                    <template #default="{ item }">
                      {{ renderTag(item.id).description }}
                    </template>
                  </data-column>

                  <data-column :sortable="false" width="20%">
                    <template #default="{ item }">
                      <div class="actions-column-tags">
                        <span v-if="item.isDefault" class="tag">
                          {{
                            item.isDefault
                              ? $l(
                                  "components.configurationUpdate.default",
                                  "Default"
                                )
                              : ""
                          }}
                        </span>
                        <Dropdown trigger="click" placement="bottom-start">
                          <span
                            href="javascript:void(0)"
                            style="cursor: pointer"
                          >
                            <octicon
                              :icon="kebabHorizontal"
                              label="kebabHorizontal"
                            />
                          </span>
                          <DropdownMenu slot="list">
                            <DropdownItem @click.native="deleteTag(item.id)">
                              <span style="color: red">{{
                                $l("components.server.delete", "Delete")
                              }}</span></DropdownItem
                            >
                            <div v-if="!item.isDefault" class="divider"></div>
                            <DropdownItem
                              v-if="!item.isDefault"
                              @click.native="setDefault(item.id)"
                            >
                              <span style="color: green">{{
                                $l(
                                  "components.server.setDefault",
                                  "Set Default"
                                )
                              }}</span>
                            </DropdownItem>
                          </DropdownMenu>
                        </Dropdown>
                      </div>
                    </template>
                  </data-column>
                </data-table>

                <modal :show.sync="formAddTag.isFormOpen" content-width="60%">
                  <box>
                    <div style="display: flex; flex-wrap: nowrap">
                      <Select
                        v-model="formAddTag.value"
                        placeholder=""
                        style="width: 50%"
                      >
                        <Option
                          v-for="item in listTags"
                          :value="item.id"
                          :key="item.id"
                          :disabled="checkDisableOption(item.id)"
                          >{{ item.name }}</Option
                        >
                      </Select>

                      <div
                        class="ml-6 mt-2"
                        style="
                          display: flex;
                          flex-wrap: nowrap;
                          justify-items: center;
                        "
                      >
                        <switches
                          v-model="formAddTag.isDefault"
                          type-bold="false"
                          theme="custom"
                          color="blue"
                        ></switches>

                        <p
                          style="
                            position: relative;
                            top: -10px;
                            left: 10px;
                            font-size: 14px;
                          "
                        >
                          {{ $l("components.server.default", "Default") }}
                        </p>
                      </div>
                    </div>

                    <div class="field is-grouped is-grouped-right">
                      <div class="control">
                        <Button
                          style="margin-right: 8px"
                          @click="formAddTag.isFormOpen = false"
                          >{{ $l("global.cancel", "Cancel") }}</Button
                        >
                      </div>
                      <div class="control">
                        <Button
                          type="primary"
                          @click="handleClickSubmitFormAddTag"
                          :disabled="!formAddTag.value"
                        >
                          {{ $l("global.submit", "Submit") }}</Button
                        >
                      </div>
                    </div>
                  </box>
                </modal>
              </FormItem>

              <FormItem>
                <div class="field is-grouped is-grouped-right">
                  <div class="control">
                    <Button
                      style="margin-right: 8px"
                      @click="handleClickBack"
                      >{{ $l("global.back", "Back") }}</Button
                    >
                  </div>
                  <div class="control">
                    <Button type="primary" @click="handleClickComplete">
                      {{ $l("global.complete", "Complete") }}</Button
                    >
                  </div>
                </div>
              </FormItem>
            </Form>
          </div>
        </box>
      </div>
    </div>
  </section>
</template>

<script>
import vSelect from "vue-select";
import { DataTable, DataColumn, Modal } from "@cyradar/ui";
import SvgIcon from "@jamescoyle/vue-icon";
import { mdiCalendarRange } from "@mdi/js";
import { customAlphabet } from "nanoid";
import ServerApi from "@/api/Server";
import UpdateApi from "@/api/Update";
import Format from "date-fns/format";
import { mapGetters } from "vuex";
import { plus, kebabHorizontal } from "octicons-vue";
import Switches from "vue-switches";
import Datepicker from "vuejs-datepicker";
import {
  Dropdown,
  DropdownMenu,
  DropdownItem,
  Checkbox,
  Form,
  FormItem,
  Input,
  Select,
  Option,
  Button,
  Row,
  Col,
  Upload,
  Icon,
} from "iview";

export default {
  components: {
    vSelect,
    SvgIcon,
    DataTable,
    DataColumn,
    Modal,
    Switches,
    Dropdown,
    DropdownMenu,
    DropdownItem,
    Checkbox,
    Form,
    FormItem,
    Input,
    Select,
    Option,
    Button,
    Row,
    Col,
    Upload,
    Icon,
    Datepicker,
  },

  data() {
    return {
      path: mdiCalendarRange,
      plus,
      kebabHorizontal,
      loading: false,
      step: 1,
      listSelectedTags: [],
      listTags: [],

      formAddTag: {
        isFormOpen: false,
        value: "",
        isDefault: false,
      },

      form: {
        // fingerprint: "",
        // agentLimit: "-1",
        // serverLimit: "0",
        // expiredAt: new Date(
        //   new Date().setFullYear(new Date().getFullYear() + 1)
        // ),
        // treeheight: "0",
      },

      listAgentLimit: [
        {
          value: "-1",
          label: "-1",
        },
      ],
    };
  },

  computed: {
    ...mapGetters(["authenticationClaims", "isAdmin", "currentLocale"]),
    defaultRule() {
      return {
        required: true,
        message: this.$l(
          "components.configurationUpdate.cannotBeLeftBlank",
          "Cannot be left blank"
        ),
        trigger: "blur",
      };
    },

    ruleValidate() {
      return {
        name: [
          this.defaultRule,
          {
            type: "string",
            validator: this.checkValidName,
            required: true,
            trigger: "blur",
          },
        ],
        fingerprint: [
          this.defaultRule,
          {
            type: "string",
            validator: this.checkValidFingerprint,
            required: true,
            trigger: "blur",
          },
        ],

        email: [
          {
            type: "string",
            validator: this.checkValidEmail,
            trigger: "blur",
          },
        ],

        phone_number: [
          {
            type: "string",
            validator: this.checkValidPhoneNumber,
            trigger: "blur",
          },
        ],

        agentLimit: [
          {
            type: "string",
            validator: this.checkValidAgentLimit,
            trigger: "change",
          },
        ],

        serverLimit: [
          {
            message: this.$l(
              "components.configurationUpdate.cannotBeLeftBlank",
              "Cannot be left blank"
            ),
            trigger: "blur",
          },
          {
            type: "string",
            validator: this.checkValidServerLimit,
            trigger: "blur",
          },
        ],
      };
    },
  },

  methods: {
    // Handle Agent Limit

    handleCreateAgentLimitOption(val) {
      if (!val) {
        return;
      }

      if (this.listAgentLimit.find((item) => item.value === val)) {
        return;
      }

      let num = parseInt(val, 10);

      if (!isNaN(num) && num > 0) {
        this.listAgentLimit.push({
          value: val,
          label: val,
        });

        this.listAgentLimit = this.listAgentLimit.filter(
          (item) => item.value === "-1" || item.value === val
        );
      }
    },

    handleOpenChangeAgentLimitOption(val) {
      this.form.agentLimit = val;
    },

    // New fingerprint
    generateUniqueId() {
      const alphabet = "ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789";
      const nanoid = customAlphabet(alphabet, 5);

      const segments = [nanoid(), nanoid(), nanoid(), nanoid(), nanoid()];
      return segments.join("-");
    },

    newFingerprint() {
      this.form.fingerprint = this.generateUniqueId();
    },

    // Check Valid

    checkValidName(rule, value, callback) {
      if (value.length < 3 || value.length > 256) {
        callback(
          new Error(
            this.$l(
              "components.server.serverValidLength",
              "Name must have a length ranging from 3 to 256 characters"
            )
          )
        );
      } else {
        const regex = /^[a-zA-Z0-9_.]+$/;
        if (!regex.test(value)) {
          callback(
            new Error(
              this.$l(
                "components.server.serverValidFormat",
                "Name cannot contain spaces and can only include alphanumeric characters, dots (.), or underscores (_)"
              )
            )
          );
        } else {
          callback();
        }
      }
    },

    checkValidFingerprint(rule, value, callback) {
      const regex =
        /^[A-Z0-9]{5}-[A-Z0-9]{5}-[A-Z0-9]{5}-[A-Z0-9]{5}-[A-Z0-9]{5}$/;
      if (!regex.test(value)) {
        callback(
          new Error(
            this.$l(
              "components.server.fingerprintValidFormat",
              "Fingerprint must be in the form XXXXX-XXX-XXXXX-XXXXX-XXXXX"
            )
          )
        );
      } else {
        callback();
      }
    },

    checkValidEmail(rule, value, callback) {
      const regex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!regex.test(value) && value) {
        callback(
          new Error(
            this.$l("components.server.emailValidFormat", "Email invalidate")
          )
        );
      } else {
        callback();
      }
    },

    checkValidPhoneNumber(rule, value, callback) {
      const regex = /^0\d{9}$/;
      if (!regex.test(value) && value) {
        callback(
          new Error(
            this.$l(
              "components.server.phoneValidFormat",
              "Phone number invalidate"
            )
          )
        );
      } else {
        callback();
      }
    },

    checkValidAgentLimit(rule, value, callback) {
      if (!value) {
        callback(
          new Error(
            this.$l(
              "components.configurationUpdate.cannotBeLeftBlank",
              "Cannot be left blank"
            )
          )
        );
      } else {
        callback();
      }
    },

    checkValidServerLimit(rule, value, callback) {
      let num = parseInt(value, 10);
      if (isNaN(num) || num < 0) {
        callback(
          new Error(
            this.$l(
              "components.configurationUpdate.limitValidFormat",
              "The quantity must be greater than or equal to 0"
            )
          )
        );
      } else {
        callback();
      }
    },

    // Handle click

    handleClickStep(step) {
      if (this.step === step) {
        return;
      }

      if (step === 1) {
        this.step = 1;
      } else {
        if (step === 2) {
          this.step = 2;
        } else {
          if (step === 3) {
            this.step = 3;
          }
        }
      }
    },

    handleClickCancel() {
      this.$router.push({ name: "server" });
    },

    handleClickNext(name) {
      this.$refs[name].validate((valid) => {
        if (valid) {
          this.step++;
        }
      });
    },

    handleClickBack() {
      this.step--;
    },

    handleClickComplete() {
      this.updateServer();
    },

    updateServer() {
      ServerApi.updateServer(this.$http, {
        ...this.form,
        agentLimit: parseInt(this.form.agentLimit),
        serverLimit: parseInt(this.form.serverLimit),
        treeheight: parseInt(this.form.serverLimit),
        expiredAt: this.form.expiredAt,
        tags: this.listSelectedTags.map((item) => {
          return {
            id: item.id,
            isDefault: item.isDefault,
            name: this.renderTag(item.id).name,
            description: this.renderTag(item.id).description,
          };
        }),
      }).then((res) => {
        this.$store.dispatch("NOTIFY", {
          type: "success",
          text: res.message,
          duration: 10000,
        });

        this.$router.replace({
          name: "server",
        });
      });
    },

    // Handle step 3

    async fetchTags() {
      await UpdateApi.getTagList(this.$http).then((data) => {
        let tags = data;
        this.listTags = tags.map((tag) => {
          return {
            name: tag.name,
            id: tag._id,
            description: tag.description,
            isDefault: tag.isDefault,
          };
        });

        const defaultTag = this.listTags.find((tag) => tag.isDefault === true);

        if (defaultTag) {
          this.formAddTag.value = defaultTag.id;
          this.formAddTag.isDefault = true;
        }
      });
    },

    showFormAddTag() {
      this.formAddTag.isFormOpen = true;

      if (this.listSelectedTags.length > 0) {
        this.formAddTag.value = "";
        this.formAddTag.isDefault = "";
      } else {
        const defaultTag = this.listTags.find((tag) => tag.isDefault === true);

        if (defaultTag) {
          this.formAddTag.value = defaultTag.id;
          this.formAddTag.isDefault = true;
        }
      }
    },

    handleClickSubmitFormAddTag() {
      if (
        !this.listSelectedTags.find((item) => item.id === this.formAddTag.id)
      ) {
        if (this.formAddTag.isDefault) {
          this.listSelectedTags.map((item) => (item.isDefault = false));
        }

        this.listSelectedTags.push({
          serial: this.listSelectedTags.length + 1,
          id: this.formAddTag.value,
          isDefault: this.formAddTag.isDefault,
        });
      }

      this.formAddTag.isFormOpen = false;
    },

    deleteTag(id) {
      this.listSelectedTags = this.listSelectedTags.filter(
        (item) => item.id !== id
      );
      this.listSelectedTags.map((item, index) => (item.serial = index + 1));

      this.$refs.table.loadItems();
    },

    setDefault(id) {
      this.listSelectedTags.map((item) => (item.isDefault = false));
      this.listSelectedTags.find((item) => item.id === id).isDefault = true;

      this.$refs.table.loadItems();
    },

    renderTag(val) {
      const x = this.listTags.find((item) => item.id === val);

      if (!x) {
        return;
      }

      return x;
    },

    checkDisableOption(val) {
      const x = this.listSelectedTags.find((item) => item.id === val);

      if (!x) {
        return false;
      }

      return true;
    },

    // Get Server

    getServer() {
      ServerApi.getServer(this.$http, this.$route.params.id).then((res) => {
        if (res.agentLimit !== -1) {
          this.listAgentLimit.push({
            value: res.agentLimit.toString(),
            label: res.agentLimit.toString(),
          });
        }

        if (res.tags && res.tags.length > 0) {
          this.form = {
            ...res,
            agentLimit: res.agentLimit.toString(),
            serverLimit: res.serverLimit ? res.serverLimit.toString() : "0",
            treeheight: res.treeHeight ? res.treeHeight.toString() : "0",
          };

          this.listSelectedTags = res.tags.map((item, index) => {
            return {
              serial: index + 1,
              id: item.id,
              isDefault:
                item.isDefault && item.isDefault === true ? true : false,
            };
          });
        } else {
          this.form = {
            ...res,
            agentLimit: res.agentLimit.toString(),
            serverLimit: res.serverLimit ? res.serverLimit.toString() : "0",
            treeheight: res.treeHeight ? res.treeHeight.toString() : "0",
          };
        }
      });
    },
  },

  mounted() {
    this.fetchTags();
    this.getServer();
  },
};
</script>

<style src="vue-select/dist/vue-select.css"></style>

<style lang="scss" scoped>
@import "assets/app.scss";

.get-fingerprint {
  &:hover {
    opacity: 0.5;
  }

  position: absolute;
  z-index: 99;
  top: 5px;
  right: 5px;
  cursor: pointer;
}

.actions-column-tags {
  display: flex !important;
  justify-content: flex-end !important;

  .menu-button:hover {
    cursor: pointer;
    opacity: 0.5;
  }

  .tag {
    background-color: #208b24;
    color: #c6f2c7;
    font-weight: 900;

    @include responsive(large-desktop) {
      width: 100px;
      height: 20px;
      font-size: 16px;
      margin-right: 70px;
    }

    @include responsive(desktop) {
      width: 80px;
      height: 20px;
      font-size: 14px;
      margin-right: 50px;
    }

    @include responsive(tablet) {
      margin-right: 30px;
    }

    @include responsive(mobile) {
      margin-right: 30px;
    }
  }
}

.close-icon {
  position: absolute;
  top: 15px;
  right: 15px;
  cursor: pointer;

  &:hover {
    opacity: 0.5;
  }
}

/deep/ button.modal-close {
  display: none;
}

/deep/ .vdp-datepicker div input {
  width: 100%;
  padding-left: 10px;
  padding-right: 10px;
  border: 1px solid #dcdee2;
  border-radius: 4px;
}

/deep/ .default-module_datatable__filter_3aG2p {
  display: none !important;
}
</style>
